import type { DEFAULT_FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { mapFlexibleDatesToTypeaheadFormat } from 'lib/search/searchUtils'
import {
  convertDateRangesToMonthString,
  mapFlexibleNightsToDurationRange,
} from 'lib/search/flexibleDateUtils'
import type {
  TripChatExperienceRecentSearchPayload,
  TripChatFlightRecentSearchPayload,
  TripChatHotelDateSelection,
  TripChatHotelRecentSearchPayload,
  TripChatRecentSearch,
  TripChatSearchSurface,
} from 'tripPlanner/api/types'
import type { ListingDrawerHotelDateSelection } from '../pages/chat/ChatTripPlannerPage/components/ListingDrawerSearchDates'
import type { DrawerSortValue } from '../pages/chat/ChatTripPlannerPage/components/ListingDrawerSearchControls'
import type { ExperienceDateRange } from './resolveSearchDefaults'

export type ApiRecentSearchLocation = {
  query: string
  placeId?: string
  lat?: number
  lng?: number
}

export type ApiRecentSearchDateSelection =
  | { type: 'specific'; checkIn: string; checkOut: string }
  | {
    type: 'flexible'
    dateRanges: Array<{ start: string; end: string }>
    numberOfNights: Array<number>
  }
  | { type: 'anytime' }

export type ApiHotelRecentSearchPayload = {
  location: ApiRecentSearchLocation
  dateSelection: ApiRecentSearchDateSelection
  occupancies?: Array<App.Occupants>
}

export type ApiExperienceRecentSearchPayload = {
  location?: ApiRecentSearchLocation
  dateSelection?: ApiRecentSearchDateSelection
  sort?: string
}

export type UpsertTripChatRecentSearchBody =
  | {
    surface: 'hotels'
    tripStopId?: number | null
    payload: ApiHotelRecentSearchPayload
  }
  | {
    surface: 'experiences'
    tripStopId?: number | null
    payload: ApiExperienceRecentSearchPayload
  }
  | {
    surface: 'flights'
    tripStopId?: number | null
    payload: {
      originAirportCode: string
      destinationAirportCode: string
      originAirportName?: string
      destinationAirportName?: string
      departureDate?: string
      returnDate?: string
      fareType?: string
      fareClass?: string
      occupants?: Array<App.Occupants>
    }
  }

function isApiLocation(value: unknown): value is ApiRecentSearchLocation {
  return !!value &&
    typeof value === 'object' &&
    'query' in value &&
    typeof (value as ApiRecentSearchLocation).query === 'string'
}

function normalizeLocationFields(payload: unknown): { locationQuery?: string; placeId?: string } {
  if (!payload || typeof payload !== 'object') return {}

  const record = payload as Record<string, unknown>
  if (isApiLocation(record.location)) {
    return {
      locationQuery: record.location.query,
      placeId: record.location.placeId,
    }
  }

  return {
    locationQuery: typeof record.locationQuery === 'string' ? record.locationQuery : undefined,
    placeId: typeof record.placeId === 'string' ? record.placeId : undefined,
  }
}

function apiDateSelectionToDrawer(
  dateSelection: ApiRecentSearchDateSelection | undefined,
): TripChatHotelDateSelection {
  if (!dateSelection || dateSelection.type === 'anytime') {
    return { type: 'anytime' }
  }

  if (dateSelection.type === 'specific') {
    return {
      type: 'specific',
      checkIn: dateSelection.checkIn,
      checkOut: dateSelection.checkOut,
    }
  }

  const flexibleMonths = convertDateRangesToMonthString(dateSelection.dateRanges) ?? ''
  const flexibleNights = mapFlexibleNightsToDurationRange(dateSelection.numberOfNights)

  return {
    type: 'flexible',
    flexibleMonths,
    flexibleNights: flexibleNights as Exclude<
      DEFAULT_FLEXIBLE_DURATION_RANGE,
      DEFAULT_FLEXIBLE_DURATION_RANGE.EMPTY
    >,
    userSelectedFlexibleMonths: !!flexibleMonths,
  }
}

function drawerDateSelectionToApi(
  selection: ListingDrawerHotelDateSelection | TripChatHotelDateSelection,
): ApiRecentSearchDateSelection {
  if (selection.type === 'specific') {
    return {
      type: 'specific',
      checkIn: selection.checkIn,
      checkOut: selection.checkOut,
    }
  }

  if (selection.type === 'flexible' && selection.flexibleMonths && selection.flexibleNights) {
    return mapFlexibleDatesToTypeaheadFormat(
      selection.flexibleMonths,
      selection.flexibleNights,
    )
  }

  return { type: 'anytime' }
}

function apiDateSelectionToExperienceRange(
  dateSelection: ApiRecentSearchDateSelection | undefined,
): ExperienceDateRange {
  if (!dateSelection || dateSelection.type === 'anytime') {
    return {}
  }

  if (dateSelection.type === 'specific') {
    return {
      checkIn: dateSelection.checkIn,
      checkOut: dateSelection.checkOut,
    }
  }

  const firstRange = dateSelection.dateRanges[0]
  return firstRange
    ? { checkIn: firstRange.start, checkOut: firstRange.end }
    : {}
}

function experienceRangeToApiDateSelection(
  dateRange: ExperienceDateRange,
): ApiRecentSearchDateSelection | undefined {
  if (!dateRange.checkIn && !dateRange.checkOut) {
    return undefined
  }

  if (dateRange.checkIn && dateRange.checkOut) {
    return {
      type: 'specific',
      checkIn: dateRange.checkIn,
      checkOut: dateRange.checkOut,
    }
  }

  return undefined
}

export function normalizeHotelRecentSearchPayload(
  payload: TripChatHotelRecentSearchPayload | ApiHotelRecentSearchPayload,
): TripChatHotelRecentSearchPayload {
  const location = normalizeLocationFields(payload)
  // Keep "no dates persisted" distinct from explicit anytime — the resolver falls
  // through to concrete stop dates when dateSelection is absent.
  const dateSelection = 'dateSelection' in payload && payload.dateSelection
    ? apiDateSelectionToDrawer(payload.dateSelection as ApiRecentSearchDateSelection)
    : undefined

  return {
    ...location,
    ...(dateSelection ? { dateSelection } : {}),
    occupancies: 'occupancies' in payload ? payload.occupancies : undefined,
  }
}

export function normalizeExperienceRecentSearchPayload(
  payload: TripChatExperienceRecentSearchPayload | ApiExperienceRecentSearchPayload,
): TripChatExperienceRecentSearchPayload {
  const location = normalizeLocationFields(payload)
  const dateSelection = 'dateSelection' in payload && payload.dateSelection
    ? payload.dateSelection
    : undefined
  const dateRange = dateSelection
    ? apiDateSelectionToExperienceRange(dateSelection)
    : {
        checkIn: 'checkIn' in payload ? payload.checkIn : undefined,
        checkOut: 'checkOut' in payload ? payload.checkOut : undefined,
      }

  return {
    ...location,
    ...dateRange,
    sort: 'sort' in payload ? payload.sort : undefined,
  }
}

export function normalizeFlightRecentSearchPayload(
  payload: TripChatFlightRecentSearchPayload,
): TripChatFlightRecentSearchPayload {
  // Wire format may send occupants as an array; drawer/resolver use a single party.
  // An empty array must not leak through as truthy and invent a zero-adults room.
  if (Array.isArray(payload.occupants)) {
    const first = payload.occupants[0]
    if (!first || typeof first.adults !== 'number') {
      const { occupants: _ignored, ...rest } = payload
      return rest
    }
    return { ...payload, occupants: first }
  }

  return payload
}

export function normalizeRecentSearchPayload(
  recentSearch: TripChatRecentSearch,
): TripChatRecentSearch {
  switch (recentSearch.surface) {
    case 'hotels':
      return {
        ...recentSearch,
        payload: normalizeHotelRecentSearchPayload(
          recentSearch.payload as TripChatHotelRecentSearchPayload | ApiHotelRecentSearchPayload,
        ),
      }
    case 'experiences':
      return {
        ...recentSearch,
        payload: normalizeExperienceRecentSearchPayload(
          recentSearch.payload as TripChatExperienceRecentSearchPayload | ApiExperienceRecentSearchPayload,
        ),
      }
    case 'flights':
      return {
        ...recentSearch,
        payload: normalizeFlightRecentSearchPayload(
          recentSearch.payload as TripChatFlightRecentSearchPayload,
        ),
      }
    default:
      return recentSearch
  }
}

export function normalizeRecentSearches(
  recentSearches: Array<TripChatRecentSearch> | undefined,
): Array<TripChatRecentSearch> | undefined {
  return recentSearches?.map(normalizeRecentSearchPayload)
}

export function buildHotelRecentSearchUpsertBody({
  tripStopId,
  searchQuery,
  placeId,
  dateSelection,
  occupancies,
}: {
  tripStopId?: number | null
  searchQuery: string
  placeId?: string
  dateSelection: ListingDrawerHotelDateSelection | TripChatHotelDateSelection
  occupancies?: Array<App.Occupants>
}): UpsertTripChatRecentSearchBody {
  return {
    surface: 'hotels',
    tripStopId,
    payload: {
      location: {
        query: searchQuery,
        ...(placeId ? { placeId } : {}),
      },
      dateSelection: drawerDateSelectionToApi(dateSelection),
      ...(occupancies?.length ? { occupancies } : {}),
    },
  }
}

export function buildExperienceRecentSearchUpsertBody({
  tripStopId,
  searchQuery,
  placeId,
  dateRange,
  sort,
}: {
  tripStopId?: number | null
  searchQuery: string
  placeId?: string
  dateRange: ExperienceDateRange
  sort?: DrawerSortValue
}): UpsertTripChatRecentSearchBody {
  const dateSelection = experienceRangeToApiDateSelection(dateRange)

  return {
    surface: 'experiences',
    tripStopId,
    payload: {
      ...(searchQuery.trim() || placeId
        ? {
            location: {
              query: searchQuery,
              ...(placeId ? { placeId } : {}),
            },
          }
        : {}),
      ...(dateSelection ? { dateSelection } : {}),
      ...(sort && sort !== 'type' ? { sort } : {}),
    },
  }
}

export function buildFlightRecentSearchUpsertBody({
  tripStopId,
  originAirportCode,
  originAirportName,
  destinationAirportCode,
  destinationAirportName,
  departureDate,
  returnDate,
  fareType,
  fareClass,
  occupants,
}: {
  tripStopId?: number | null
  originAirportCode: string
  destinationAirportCode: string
  originAirportName?: string
  destinationAirportName?: string
  departureDate?: string
  returnDate?: string
  fareType?: string
  fareClass?: string
  occupants?: App.Occupants
}): UpsertTripChatRecentSearchBody {
  return {
    surface: 'flights',
    tripStopId,
    payload: {
      originAirportCode,
      destinationAirportCode,
      ...(originAirportName ? { originAirportName } : {}),
      ...(destinationAirportName ? { destinationAirportName } : {}),
      ...(departureDate ? { departureDate } : {}),
      ...(returnDate ? { returnDate } : {}),
      ...(fareType ? { fareType } : {}),
      ...(fareClass ? { fareClass } : {}),
      ...(occupants ? { occupants: [occupants] } : {}),
    },
  }
}

export function getRecentSearchSurfaceStopId(
  surface: TripChatSearchSurface,
  tripStopId?: number | null,
): number | null {
  return tripStopId ?? null
}
